import { Component, Inject, computed } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import {
  AdActionType,
  CampaignBannerMediaType,
  CampaignContentType,
  CampaignType,
} from "../../../../shared/enums/campaign.enums";
import {
  CampaignPostContentType,
  LinkedInPostContentType,
} from "../../../../shared/enums/campaignPost.enums";
import { AppLanguage, AppLocale } from "../../../../shared/models/appData";
import { BrandCampaign } from "../../../../shared/models/brandCampaign";
import { BrandCampaignCategory } from "../../../../shared/models/brandCampaignCategory";
import { LanguageService } from "../../../../shared/services/language.service";

export interface CampaignAddContentDialogData {
  readonly campaign: BrandCampaign;
  readonly category: BrandCampaignCategory;
}

export interface CampaignAddContentDialogResult {
  readonly contentType: CampaignContentType;
  readonly postContentType?:
    | LinkedInPostContentType
    | CampaignPostContentType
    | CampaignBannerMediaType;
}

@Component({
  selector: "app-campaign-add-content-dialog",
  templateUrl: "./campaign-add-content-dialog.component.html",
  styleUrls: ["./campaign-add-content-dialog.component.scss"],
})
export class CampaignAddContentDialogComponent {
  private readonly campaign: BrandCampaign;
  public readonly category: BrandCampaignCategory;
  public readonly CampaignContentType = CampaignContentType;
  public readonly CONTENT_TYPES: CampaignContentType[] =
    Object.values(CampaignContentType);
  public readonly POST_CONTENT_TYPES: CampaignPostContentType[];
  public readonly LINKEDIN_CONTENT_TYPES: LinkedInPostContentType[] =
    Object.values(LinkedInPostContentType);
  public contentType = CampaignContentType.Post;
  public postMediaType = CampaignPostContentType.Image;
  public bannerMediaType = CampaignBannerMediaType.FacebookHeader;
  public linkedInPostContentType = LinkedInPostContentType.Image;
  public readonly assetsLanguage = computed(() =>
    this.languageService.locale === AppLocale.DeDe
      ? AppLanguage.De
      : AppLanguage.En,
  );

  constructor(
    @Inject(MAT_DIALOG_DATA) data: CampaignAddContentDialogData,
    private readonly dialogRef: MatDialogRef<
      CampaignAddContentDialogComponent,
      CampaignAddContentDialogResult
    >,
    private readonly languageService: LanguageService,
  ) {
    this.campaign = data.campaign;
    this.category = data.category;
    this.POST_CONTENT_TYPES = Object.values(CampaignPostContentType).filter(
      (value) => value !== CampaignPostContentType.ResponsiveImage,
    );
  }

  public onCreateContent(): void {
    let postContentType:
      | LinkedInPostContentType
      | CampaignPostContentType
      | CampaignBannerMediaType
      | undefined;

    if (this.contentType === CampaignContentType.Post) {
      postContentType = this.postMediaType;
    }

    if (this.contentType === CampaignContentType.Banner) {
      postContentType = this.bannerMediaType;
    }

    if (this.contentType === CampaignContentType.LinkedinPost) {
      postContentType = this.linkedInPostContentType;
    }

    this.dialogRef.close({
      contentType: this.contentType,
      postContentType: postContentType,
    });
  }

  public selectTab(contentType: CampaignContentType): void {
    if (this.isTabActive(contentType)) {
      this.contentType = contentType;
    }
  }

  public isTabActive(contentType: CampaignContentType): boolean {
    return !(
      this.campaign.type === CampaignType.DownloadOnly &&
      contentType === CampaignContentType.GoogleAd
    );
  }

  public isTabSelected(contentType: CampaignContentType): boolean {
    return this.contentType === contentType;
  }

  public selectPostTab(postMediaType: CampaignPostContentType): void {
    if (
      this.campaign.type === CampaignType.DownloadOnly &&
      postMediaType === CampaignPostContentType.Carousel
    ) {
      return;
    }
    this.postMediaType = postMediaType;
  }

  public isPostTabActive(contentType: CampaignPostContentType): boolean {
    // GIF AND 360 IMAGES ARE DISABLED FOR CAMPAIGNS WITH BOOST POST ADS
    if (
      this.campaign.adActionType === AdActionType.Boost &&
      (contentType === CampaignPostContentType.Gif ||
        contentType === CampaignPostContentType.Image360)
    ) {
      return false;
    }

    return !(
      this.campaign.type === CampaignType.DownloadOnly &&
      contentType === CampaignPostContentType.Carousel
    );
  }

  public isPostTabSelected(postMediaType: CampaignPostContentType): boolean {
    return this.postMediaType === postMediaType;
  }

  public getPostImageUrl(mediaType: CampaignPostContentType): string {
    const lang = this.assetsLanguage();
    switch (mediaType) {
      case CampaignPostContentType.Image:
      case CampaignPostContentType.LinkPost:
      case CampaignPostContentType.Image360:
      case CampaignPostContentType.Gif:
        return `assets/images/post-mockups/post-image-mockup_${lang}@2x.jpg`;
      case CampaignPostContentType.Carousel:
        return `assets/images/post-mockups/post-carousel-mockup_${lang}@2x.jpg`;
      case CampaignPostContentType.Video:
        return `assets/images/post-mockups/post-video-mockup_${lang}@2x.jpg`;
    }
    return "";
  }

  public getLinkedinPostImageUrl(mediaType: LinkedInPostContentType): string {
    return `assets/images/post-mockups/im_linkedin_${mediaType}_mockup_${this.assetsLanguage()}.jpg`.toLowerCase();
  }
}
